/**
 * selectionWalls.ts -- Phase 49: Text selection walls module.
 *
 * Browser text selections become interactive game zones:
 * - Visible semi-transparent overlay on the canvas matching selection bounding rects
 * - Enemy pills whose center overlaps a selection rect are auto-destroyed with particles
 * - Player cursor inside a selection zone is invincible (no collision damage)
 * - First cursor-in-zone triggers discovery hint via queueLine and bumps paragraph line-height
 * - Line-height reverts when selection clears; all DOM effects clean up on game exit
 *
 * Detection uses the `selectionchange` DOM event, not per-frame polling (SEL-05).
 */

import type { GameState } from './types';
import { spawnParticle } from './particles';
import { deactivateWord } from './wordPool';
import { queueLine } from './dialogue';
import { SELECTION_DISCOVERY_HINT } from './dialogueScript';

// ---------------------------------------------------------------------------
// Module-level state (reset via resetSelectionWalls)
// ---------------------------------------------------------------------------

let affectedParagraphs: Array<{ el: HTMLElement; originalLineHeight: string }> = [];

// ---------------------------------------------------------------------------
// Private helper
// ---------------------------------------------------------------------------

function pointInRect(
  px: number,
  py: number,
  rect: { x: number; y: number; width: number; height: number }
): boolean {
  return px >= rect.x && px <= rect.x + rect.width && py >= rect.y && py <= rect.y + rect.height;
}

// ---------------------------------------------------------------------------
// handleSelectionChange -- called from selectionchange DOM event listener
// ---------------------------------------------------------------------------

export function handleSelectionChange(state: GameState, canvas: HTMLCanvasElement): void {
  const selection = document.getSelection();

  // Collapsed or null selection: clear overlay and revert line-heights
  if (!selection || selection.isCollapsed) {
    state.selectionRects = [];
    for (const entry of affectedParagraphs) {
      entry.el.style.lineHeight = entry.originalLineHeight;
    }
    affectedParagraphs = [];
    state.selectionDirty = true;
    return;
  }

  const range = selection.getRangeAt(0);
  const domRects = range.getClientRects();
  const canvasRect = canvas.getBoundingClientRect();

  // Map DOM rects to canvas-local coordinates, filter noise (SEL-05 + T-49-03)
  const mapped: Array<{ x: number; y: number; width: number; height: number }> = [];
  for (let i = 0; i < domRects.length; i++) {
    const r = domRects[i];
    if (r.width < 5 || r.height < 5) continue;
    mapped.push({
      x: r.x - canvasRect.x,
      y: r.y - canvasRect.y,
      width: r.width,
      height: r.height,
    });
  }

  state.selectionRects = mapped;
  state.selectionDirty = true;

  // Line-height effect (SEL-04): bump the paragraph containing the selection anchor
  // Walk up from anchorNode until we find a <p> element
  let node: Node | null = selection.anchorNode;
  let paragraphEl: HTMLElement | null = null;
  while (node && node !== document.body) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === 'P') {
        paragraphEl = el;
        break;
      }
    }
    node = node.parentElement;
  }

  if (paragraphEl !== null) {
    const alreadyTracked = affectedParagraphs.some((e) => e.el === paragraphEl);
    if (!alreadyTracked) {
      affectedParagraphs.push({
        el: paragraphEl,
        originalLineHeight: paragraphEl.style.lineHeight || '',
      });
      paragraphEl.style.lineHeight = '1.4';
    }
  }
}

// ---------------------------------------------------------------------------
// tickSelectionWalls -- auto-destroy enemies whose center overlaps a selection rect
// ---------------------------------------------------------------------------

export function tickSelectionWalls(state: GameState): void {
  if (state.selectionRects.length === 0) return;

  // Iterate backward to splice safely
  for (let i = state.enemies.length - 1; i >= 0; i--) {
    const enemy = state.enemies[i];
    const inside = state.selectionRects.some((rect) => pointInRect(enemy.x, enemy.y, rect));
    if (!inside) continue;

    // Spawn particles for untyped letters only
    for (let c = enemy.typed; c < enemy.word.length; c++) {
      spawnParticle(
        enemy.word[c],
        enemy.x + (c - enemy.typed) * 8,
        enemy.y,
        state.particles,
        undefined,
        undefined,
        enemy.fontSize
      );
    }

    deactivateWord(enemy.word);
    state.enemies.splice(i, 1);
  }
}

// ---------------------------------------------------------------------------
// isInsideSelection -- returns true when the player cursor is inside any selection rect
// ---------------------------------------------------------------------------

export function isInsideSelection(state: GameState): boolean {
  return state.selectionRects.some((rect) => pointInRect(state.mouseX, state.mouseY, rect));
}

// ---------------------------------------------------------------------------
// checkSelectionDiscovery -- fire discovery hint the first time cursor enters a zone
// ---------------------------------------------------------------------------

export function checkSelectionDiscovery(state: GameState): void {
  if (state.hasDiscoveredSelection) return;
  if (state.selectionRects.length > 0 && isInsideSelection(state)) {
    state.hasDiscoveredSelection = true;
    queueLine(SELECTION_DISCOVERY_HINT);
  }
}

// ---------------------------------------------------------------------------
// drawSelectionOverlay -- render all active selection zones on the canvas
// ---------------------------------------------------------------------------

export function drawSelectionOverlay(
  ctx: CanvasRenderingContext2D,
  rects: GameState['selectionRects']
): void {
  for (const rect of rects) {
    // Fill
    ctx.fillStyle = 'rgba(100,150,255,0.12)';
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

    // Pulsing border (alpha oscillates between 0.2 and 0.5)
    const pulseAlpha = 0.2 + 0.15 * (Math.sin(performance.now() / 600) * 0.5 + 0.5);
    ctx.strokeStyle = `rgba(100,150,255,${pulseAlpha.toFixed(3)})`;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
  }
}

// ---------------------------------------------------------------------------
// resetSelectionWalls -- revert DOM effects and clear module state
// ---------------------------------------------------------------------------

export function resetSelectionWalls(): void {
  for (const entry of affectedParagraphs) {
    entry.el.style.lineHeight = entry.originalLineHeight;
  }
  affectedParagraphs = [];
  // GameState fields (selectionRects, selectionDirty, hasDiscoveredSelection) are reset
  // by GameCanvas resetGameInPlace -- this function handles only module-level DOM state
}

// ---------------------------------------------------------------------------
// cleanupSelectionWalls -- called from GameCanvas cleanup on game exit
// ---------------------------------------------------------------------------

export function cleanupSelectionWalls(): void {
  resetSelectionWalls();
}
